import { useState, useEffect } from "react";
import { Modal, Form } from "react-bootstrap";
import { Duration } from "@js-joda/core";
import { Api } from "./Data/Api";
import { createLocalDateTime } from "./Data/Date";
import { showDurationShort } from "./Data/Date";
import { cents, showMoney } from "./Data/Money";
import { emitLocalDateTime } from "./Data/ApiConverters";
import { InfoCircle, Calendar3, Clock, Calculator } from "react-bootstrap-icons";
import { HeaderText } from "./Controls/HeaderText";
import { Button } from "./Controls/Button";
import "./FacilityRateTesterModal.scss";

type Props = {
    show: boolean;
    onHide: () => void;
    facilityId: number;
};

const today = () => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
};
const defaultTime = "08:00";

export function FacilityRateTesterModal({ show, onHide, facilityId }: Props) {
    const [startDate, setStartDate] = useState(today());
    const [startTime, setStartTime] = useState(defaultTime);
    const [endDate, setEndDate] = useState(today());
    const [endTime, setEndTime] = useState("12:00");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<{ price: number; duration: string } | null>(null);
    const [slideOpen, setSlideOpen] = useState(false);

    useEffect(() => {
        if (loading || result) {
            const t = requestAnimationFrame(() => {
                requestAnimationFrame(() => setSlideOpen(true));
            });
            return () => cancelAnimationFrame(t);
        } else {
            setSlideOpen(false);
        }
    }, [loading, result]);

    const handleCalculate = async () => {
        setError(null);
        setResult(null);
        const startLdt = createLocalDateTime(startDate, startTime);
        const endLdt = createLocalDateTime(endDate, endTime);
        if (!endLdt.isAfter(startLdt)) {
            setError("Exit date and time must be after entry.");
            return;
        }
        const startStr = emitLocalDateTime(startLdt);
        const endStr = emitLocalDateTime(endLdt);
        setLoading(true);
        const res = await Api.facilityRatePreview(facilityId, startStr!, endStr!);
        setLoading(false);
        if (res.isOk) {
            const duration = Duration.between(startLdt, endLdt);
            setResult({
                price: res.value.price,
                duration: showDurationShort(duration),
            });
        } else {
            setError("No applicable rates are available for these times.");
        }
    };

    const handleReset = () => {
        if (loading || result) {
            setSlideOpen(false);
            setTimeout(() => {
                setStartDate(today());
                setStartTime(defaultTime);
                setEndDate(today());
                setEndTime("12:00");
                setError(null);
                setResult(null);
            }, 350);
        } else {
            setStartDate(today());
            setStartTime(defaultTime);
            setEndDate(today());
            setEndTime("12:00");
            setError(null);
            setResult(null);
        }
    };

    const handleClose = () => {
        setError(null);
        setResult(null);
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} centered size="lg" className="rate-tester-modal">
            <Modal.Header closeButton>
                <HeaderText>Hourly Rate Calculator</HeaderText>
            </Modal.Header>
            <Modal.Body>
                <div
                    className="text-secondary px-2 mb-4"
                    style={{
                        display: "grid",
                        gridTemplateColumns: "auto 1fr",
                        alignItems: "center",
                        gap: "1rem",
                    }}
                >
                    <Calculator size={32} className="text-primary" />
                    <span>
                        Enter a parking duration to see the calculated price based on your rate structure.
                    </span>
                </div>

                <div className="rate-tester-columns">
                    <div className="rate-tester-column entry-time">
                        <div className="rate-tester-column-title">Entry Time</div>
                        <div className="rate-tester-field">
                            <Form.Label>Date</Form.Label>
                           
                                <Form.Control
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                />
                              
                          
                        </div>
                        <div className="rate-tester-field">
                            <Form.Label>Time</Form.Label>
                            
                                <Form.Control
                                    type="time"
                                    value={startTime}
                                    onChange={(e) => setStartTime(e.target.value)}
                                />
                         </div>
                    </div>
                    <div className="rate-tester-column exit-time">
                        <div className="rate-tester-column-title">Exit Time</div>
                        <div className="rate-tester-field">
                            <Form.Label>Date</Form.Label>
                         
                                <Form.Control
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                />
                            
                       
                        </div>
                        <div className="rate-tester-field">
                            <Form.Label>Time</Form.Label>
                          
                                <Form.Control
                                    type="time"
                                    value={endTime}
                                    onChange={(e) => setEndTime(e.target.value)}
                                />
                       
                         
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="alert alert-danger py-2 mb-3" role="alert">
                        {error}
                    </div>
                )}

                {(loading || result) && (
                    <div className={`rate-tester-result-slide ${slideOpen ? "rate-tester-result-slide--open" : ""} ${loading ? "rate-tester-result-slide--loading" : "rate-tester-result-slide--ready"}`}>
                        {loading ? (
                            <div className="rate-tester-skeleton">
                                <div className="rate-tester-skeleton-row">
                                    <span className="rate-tester-skeleton-label" />
                                    <span className="rate-tester-skeleton-value rate-tester-skeleton-value--sm" />
                                </div>
                                <div className="rate-tester-skeleton-row">
                                    <span className="rate-tester-skeleton-label" />
                                    <span className="rate-tester-skeleton-value rate-tester-skeleton-value--lg" />
                                </div>
                                <div className="rate-tester-skeleton-note">
                                    <span className="rate-tester-skeleton-value rate-tester-skeleton-value--note" />
                                </div>
                            </div>
                        ) : result ? (
                            <div className="rate-tester-result">
                                <div className="rate-tester-result-row">
                                    <span className="text-muted">Duration</span>
                                    <span className="fw-semibold">{result.duration}</span>
                                </div>
                                <div className="rate-tester-result-row">
                                    <span className="text-muted">Base price</span>
                                    <span className="rate-tester-result-price">{showMoney(cents(result.price))}</span>
                                </div>
                                <div className="rate-tester-fee-note">
                                    <InfoCircle size={14} />
                                    <span>This calculation does not include applicable fees.</span>
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="rate-tester-footer">
                <Button
                    type="button"
                
                    onClick={handleClose}
                >
                    Close
                </Button>
                <div className="rate-tester-footer-actions">
                    <Button
                        type="button"
                        className="rate-tester-btn-reset"
                        onClick={handleReset}
                    >
                        Reset
                    </Button>
                    <Button
                        type="button"
                        className="rate-tester-btn-calculate btn-main"
                        onClick={handleCalculate}
                        loading={loading}
                        disabled={loading}
                    >
                        {loading ? "Calculating…" : "Calculate Rate"}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}
